import React from 'react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis } from 'recharts';
import { useTheme } from '@material-ui/styles';
import { connect } from 'react-redux';
import selectors from 'store/selectors';
import chroma from 'chroma-js';
import { getRiskToleranceRanges } from './utils';
interface Props {
  percentage?: any;
  percentage_customer_2?: any;
  suitability_score?: any;
  caseCustomers?: any;
  caseInfo?: any;
  showSuitability?: boolean;
  showRisk?: boolean;
}

const RiskTolerance: React.FC<Props> = ({
  percentage,
  percentage_customer_2,
  suitability_score,
  caseCustomers,
  caseInfo,
  showSuitability = false,
  showRisk = false
}) => {
  const theme = useTheme() as any;
  const isJointCustomer = !!caseCustomers.find(
    (customer: any) => customer.is_jointed
  );

  const data =
    caseInfo.questionnaireLength === 10
      ? [
          {
            name: 'Risk Categories',
            VeryLow: 20,
            Low: 20,
            Average: 20,
            High: 20,
            VeryHigh: 20
          }
        ]
      : [
          {
            name: 'Risk Categories',
            Lowest: 100 / 7,
            VeryLow: 100 / 7,
            Low: 100 / 7,
            Average: 100 / 7,
            High: 100 / 7,
            VeryHigh: 100 / 7,
            Highest: 100 / 7
          }
        ];

  const dataKeys = Object.keys(data[0]).filter((key: string) => key !== 'name');

  const riskAnalysisColours =
    theme.palette.baseColors.charts['risk-tolerance-bar'];
  const lastIndex = riskAnalysisColours[4] ? 4 : riskAnalysisColours.length - 1;
  const lastColor = riskAnalysisColours[lastIndex];

  const colors = chroma
    .scale([
      riskAnalysisColours[0], // Start color
      chroma.mix(riskAnalysisColours[0], lastColor, 0.5),
      lastColor // End color
    ])
    .colors(caseInfo.questionnaireLength === 10 ? 5 : 7);

  const categories = getRiskToleranceRanges(
    caseInfo.questionnaireLength,
    colors
  );

  const getArrowPosition = (percentage: number) => {
    // Find the category the percentage falls into
    const totalCategories = categories.length;
    const categoryIndex = categories.findIndex(
      cat => percentage >= cat.rangeStart && percentage <= cat.rangeEnd
    );

    if (categoryIndex === -1) return '0%'; // Default to 0 if not found

    const { rangeStart, rangeEnd } = categories[categoryIndex];

    // Find relative position within the category
    const categoryWidth = 100 / totalCategories; // Even distribution of space
    let relativePosition =
      ((percentage - rangeStart) / (rangeEnd - rangeStart)) * categoryWidth;

    // **Avoid overlap for boundary values**
    if (percentage == rangeEnd && categoryIndex < totalCategories - 1) {
      relativePosition -= 0.5; // Shift left slightly for end values
    } else if (percentage == rangeStart && categoryIndex > 0) {
      relativePosition += 0.5; // Shift right slightly for start values
    }

    // Compute final position
    return `calc(${categoryIndex * categoryWidth + relativePosition}% - 10px)`;
  };

  const Arrow: React.FC<{ value: any; colorIndex: number }> = ({
    value,
    colorIndex
  }) => {
    return (
      <div
        style={{
          position: 'absolute',
          top: '10px',
          left: getArrowPosition(value),
          transform: 'rotate(180deg)'
        }}
      >
        <svg width="20" height="10">
          <polygon
            points="10,0 20,10 0,10"
            fill={riskAnalysisColours[colorIndex]}
          />
        </svg>
      </div>
    );
  };

  return (
    <div style={{ position: 'relative' }}>
      <ResponsiveContainer width="100%" height={100}>
        <BarChart
          data={data}
          layout="vertical"
          margin={{
            top: 20,
            right: 0,
            bottom: 50,
            left: 0
          }}
        >
          <XAxis type="number" domain={[0, 100]} hide />
          <YAxis type="category" hide dataKey="name" />
          {dataKeys.map((key: string, index: number) => (
            <Bar
              key={key}
              dataKey={key}
              stackId="1"
              fill={colors[index]}
              radius={
                index === 0
                  ? [50, 0, 0, 50]
                  : index === dataKeys.length - 1
                    ? [0, 50, 50, 0]
                    : undefined
              }
              style={{ stroke: '#fff', strokeWidth: 4 }}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>

      {/* Labels with Proper Alignment */}
      <div
        style={{
          position: 'absolute',
          bottom: '26px',
          left: 0,
          width: '100%',
          display: 'flex',
          gap: '4px'
        }}
      >
        {categories.map(({ label, color, rangeStart, rangeEnd }, index) => {
          let isActive = false;

          if (showRisk) {
            isActive =
              (percentage >= rangeStart && percentage <= rangeEnd) ||
              (isJointCustomer &&
                percentage_customer_2 >= rangeStart &&
                percentage_customer_2 <= rangeEnd);
          }

          return (
            <div
              key={index}
              style={{
                width: '20%',
                textAlign: 'center',
                border: '2px solid',
                borderColor: isActive ? color : 'transparent',
                padding: '5px 0',
                fontSize: '12px',
                fontWeight: isActive ? 'bold' : 'normal',
                borderRadius: '0 0 10px 10px'
              }}
            >
              {label}
            </div>
          );
        })}
      </div>

      {showRisk && (
        <>
          {percentage && percentage > 0 && (
            <Arrow value={percentage} colorIndex={0} />
          )}

          {isJointCustomer && percentage_customer_2 > 0 && (
            <Arrow value={percentage_customer_2} colorIndex={1} />
          )}
        </>
      )}

      {showSuitability && suitability_score > 0 && (
        <Arrow value={suitability_score} colorIndex={lastIndex} />
      )}
    </div>
  );
};

export default connect((state: any) => ({
  caseCustomers: selectors.caseSelectors.caseCustomers(state),
  caseInfo: selectors.caseSelectors.case(state)
}))(RiskTolerance);
